import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  ApiError,
  login,
  logout,
  meQuery,
  register,
  requestPasswordReset,
  requestVerification,
  resetPassword,
  verifyEmail,
} from '../lib/api.js';
import { cn } from '../lib/cn.js';
import { Button } from './button.jsx';

type Mode = 'login' | 'register' | 'forgot' | 'reset';

const field =
  'w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-ink outline-none focus:border-team-blue';

// Emailed links land here as query params (the client has no router). They are
// read once at boot and stripped from the URL, so a reload or a copied link
// never replays them.
const bootParams = new URLSearchParams(window.location.search);
const verifyToken = bootParams.get('verify');
const resetToken = bootParams.get('reset');
if (verifyToken || resetToken) {
  bootParams.delete('verify');
  bootParams.delete('reset');
  const query = bootParams.toString();
  history.replaceState(
    null,
    '',
    `${location.pathname}${query ? `?${query}` : ''}${location.hash}`,
  );
}
let verifyFired = false;

const describe = (error: unknown): string =>
  error instanceof ApiError
    ? error.status === 503
      ? 'This server runs without accounts.'
      : error.message
    : 'Could not reach the server.';

/**
 * Sign in / create account, embedded in the main menu. Entirely optional —
 * guests keep playing exactly as before — but an account owns its callsign and
 * is where progression (level, wins, rank) will accrue.
 */
export const AccountPanel = () => {
  const queryClient = useQueryClient();
  const me = useQuery(meQuery);
  const [mode, setMode] = useState<Mode | null>(resetToken ? 'reset' : null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  /** One-shot feedback line: verification results, "reset link sent", … */
  const [notice, setNotice] = useState<string | null>(null);

  const refreshMe = () =>
    queryClient.invalidateQueries({ queryKey: meQuery.queryKey });

  // Redeem an emailed verification link the moment the menu mounts.
  useEffect(() => {
    if (!verifyToken || verifyFired) return;
    verifyFired = true;
    verifyEmail(verifyToken)
      .then(() => {
        setNotice('Email verified.');
        void refreshMe();
      })
      .catch((error) => setNotice(describe(error)));
  }, []);

  const submit = useMutation({
    mutationFn: async () => {
      if (mode === 'register') return register(name.trim(), email, password);
      if (mode === 'login') return login(email, password);
      if (mode === 'reset') return resetPassword(resetToken ?? '', password);
      await requestPasswordReset(email);
      return null;
    },
    onSuccess: () => {
      if (mode === 'forgot') {
        setNotice('If that email has an account, a reset link is on its way.');
      } else if (mode === 'reset') {
        setNotice('Password updated — you are signed in.');
      } else if (mode === 'register') {
        setNotice('Check your inbox for a verification link.');
      }
      setMode(null);
      setPassword('');
      void refreshMe();
    },
  });

  const signOut = useMutation({
    mutationFn: logout,
    onSuccess: () => refreshMe(),
  });

  const resend = useMutation({
    mutationFn: requestVerification,
    onSuccess: () => setNotice('Verification email sent — check your inbox.'),
    onError: (error) => setNotice(describe(error)),
  });

  if (me.data && mode === null) {
    const { account, profile } = me.data;
    return (
      <div className="mt-5 rounded-lg border border-white/10 bg-white/5 px-3.5 py-2.5">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">{account.name}</div>
            <div className="text-xs text-ink-muted">
              Level {profile?.level ?? 1} · {profile?.wins ?? 0} wins ·{' '}
              {profile?.kills ?? 0} kills
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={() => signOut.mutate()}
            disabled={signOut.isPending}
          >
            Sign out
          </Button>
        </div>
        {notice && <div className="mt-2 text-xs text-team-blue">{notice}</div>}
        {!account.verified && (
          <div className="mt-2 flex items-center justify-between gap-3 border-t border-white/10 pt-2 text-xs text-ink-muted">
            <span>Your email is not verified yet.</span>
            <button
              type="button"
              onClick={() => resend.mutate()}
              disabled={resend.isPending}
              className="shrink-0 text-team-blue hover:underline disabled:opacity-50"
            >
              Resend link
            </button>
          </div>
        )}
      </div>
    );
  }

  if (mode === null) {
    return (
      <div className="mt-5 rounded-lg border border-white/10 bg-white/5 px-3.5 py-2.5">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-ink-muted">
            Playing as a guest — an account keeps your callsign and stats.
          </span>
          <div className="flex shrink-0 gap-2">
            <Button variant="ghost" onClick={() => setMode('login')}>
              Sign in
            </Button>
            <Button variant="ghost" onClick={() => setMode('register')}>
              Sign up
            </Button>
          </div>
        </div>
        {notice && <div className="mt-2 text-xs text-team-blue">{notice}</div>}
      </div>
    );
  }

  const error = submit.error ? describe(submit.error) : null;
  const heading: Record<Mode, string> = {
    login: 'Sign in',
    register: 'Create account',
    forgot: 'Reset password',
    reset: 'Choose a new password',
  };
  const cta: Record<Mode, string> = {
    login: 'Sign in',
    register: 'Create',
    forgot: 'Send reset link',
    reset: 'Save password',
  };

  return (
    <form
      className="mt-5 rounded-lg border border-white/10 bg-white/5 p-3.5"
      onSubmit={(event) => {
        event.preventDefault();
        setNotice(null);
        submit.mutate();
      }}
    >
      <div className="flex gap-4 text-[11px] uppercase tracking-[0.2em]">
        {mode === 'forgot' || mode === 'reset' ? (
          <span className="pb-1 text-team-blue">{heading[mode]}</span>
        ) : (
          (['login', 'register'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setMode(tab)}
              className={cn(
                'pb-1',
                mode === tab
                  ? 'border-b border-team-blue text-team-blue'
                  : 'text-ink-muted hover:text-ink',
              )}
            >
              {heading[tab]}
            </button>
          ))
        )}
        <button
          type="button"
          onClick={() => setMode(null)}
          className="ml-auto text-ink-muted hover:text-ink"
        >
          ✕
        </button>
      </div>

      <div className="mt-3 space-y-2">
        {mode === 'register' && (
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Callsign"
            maxLength={20}
            autoComplete="username"
            className={field}
          />
        )}
        {mode !== 'reset' && (
          <input
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="Email"
            type="email"
            autoComplete="email"
            className={field}
          />
        )}
        {mode !== 'forgot' && (
          <input
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder={mode === 'login' ? 'Password' : 'Password (8+ characters)'}
            type="password"
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            className={field}
          />
        )}
        {mode === 'login' && (
          <button
            type="button"
            onClick={() => {
              setMode('forgot');
              setNotice(null);
            }}
            className="text-xs text-ink-muted hover:text-ink hover:underline"
          >
            Forgot your password?
          </button>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between gap-3">
        <span className="min-h-[1em] text-xs text-team-red">{error}</span>
        <Button type="submit" disabled={submit.isPending}>
          {submit.isPending ? '…' : cta[mode]}
        </Button>
      </div>
    </form>
  );
};
